/**
 * RentIncreasePanel — File PMS pour les Avis de renouvellement de bail.
 * Batch #14.168.
 *
 * Affiche les demandes en cours (avant signature complète) avec actions :
 *  - Voir détails / Générer la lettre PDF
 *  - Envoyer pour signature (1 ou 2 locataires en parallèle)
 *  - Supprimer (soft delete + annule workflow signature)
 */
import { useEffect, useState, useCallback, Fragment } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  TrendingUp, CheckCircle2, Clock, FileSignature, Loader2,
  ExternalLink, History,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import NotificationTimeline from "@/components/admin/NotificationTimeline";
import ArchiveButton from "@/components/admin/ArchiveButton";
import AdminTaskStatusSelector from "@/components/admin/AdminTaskStatusSelector";

const STATUS_LABELS = {
  pending: { label: "À traiter", color: "bg-amber-50 text-amber-700", Icon: Clock },
  letter_ready: { label: "Lettre prête", color: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  signature_pending_tenant: { label: "Locataire à signer", color: "bg-blue-50 text-blue-700", Icon: FileSignature },
  signature_pending_tenants: { label: "Locataires à signer", color: "bg-blue-50 text-blue-700", Icon: FileSignature },
  signature_tenant_accepte: { label: "Locataire OK", color: "bg-indigo-50 text-indigo-700", Icon: FileSignature },
  signature_tenant_refuse: { label: "Locataire a refusé", color: "bg-rose-50 text-rose-700", Icon: FileSignature },
  signature_tenants_signed: { label: "Locataires OK", color: "bg-indigo-50 text-indigo-700", Icon: FileSignature },
  signature_completed: { label: "Signé", color: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
};

function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0 });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CA", { dateStyle: "medium" }); }
  catch { return iso; }
}

export default function RentIncreasePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/rent-increase-requests`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vue en lecture : la gestion se fait depuis la fiche Immeuble (lien "→ Immeuble" ci-dessous).

  if (loading) {
    return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="p-10 text-center" data-testid="rent-increase-panel-empty">
        <TrendingUp className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
        <p className="text-sm text-slate-600">Aucune demande de renouvellement en cours.</p>
        <p className="text-xs text-slate-400 mt-1">Les propriétaires créent ces demandes depuis leur portail (Immeuble → Renouvellements).</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="rent-increase-panel">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">Master</th>
            <th className="px-3 py-2 text-left">Locataire(s)</th>
            <th className="px-3 py-2 text-left">Logement</th>
            <th className="px-3 py-2 text-right">Loyer actuel</th>
            <th className="px-3 py-2 text-right">Nouveau loyer</th>
            <th className="px-3 py-2 text-left">Effet</th>
            <th className="px-3 py-2 text-left">Statut</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const meta = STATUS_LABELS[it.status] || { label: it.status || "—", color: "bg-slate-100 text-slate-700", Icon: Clock };
            const StatusIcon = meta.Icon;
            return (
              <Fragment key={it.id}>
              <tr className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`rent-increase-row-${it.id}`}>
                <td className="px-3 py-2 text-xs">
                  <div className="font-medium text-slate-800 truncate max-w-[140px]">{it._master_name || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-medium text-slate-800">{it.tenant_name || "—"}</div>
                  {it.tenant_2_name && <div className="text-[10px] text-slate-500">+ {it.tenant_2_name}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[180px]">
                  {it.property_address?.split("\n")[0] || "—"}
                  {it.unit_label && <span className="text-slate-400"> · #{it.unit_label}</span>}
                </td>
                <td className="px-3 py-2 text-xs text-right tabular-nums text-slate-600">{fmtMoney(it.current_rent)}</td>
                <td className="px-3 py-2 text-xs text-right tabular-nums font-semibold text-slate-900">{fmtMoney(it.new_rent)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(it.effective_date)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {meta.label}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1 items-center flex-wrap justify-end">
                    <AdminTaskStatusSelector
                      itemType="rent_increase"
                      itemId={it.id}
                      value={it.admin_task_status}
                      onChanged={load}
                    />
                    {/* Batch #14.210 — Actions PDF/Envoi/Drive retirées (gestion dans Immeuble) */}
                    {it.master_id && (
                      <Link
                        to={`/admin/immeubles/${it.master_id}/demandes`}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-700"
                        title="Ouvrir la fiche Immeuble"
                        data-testid={`rent-increase-immeuble-${it.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    <button
                      onClick={() => setExpandedId(expandedId === it.id ? null : it.id)}
                      className={`p-1.5 rounded hover:bg-cyan-50 ${expandedId === it.id ? "bg-cyan-100 text-cyan-700" : "text-cyan-600"}`}
                      title="Voir le suivi des notifications"
                      data-testid={`rent-increase-timeline-${it.id}`}
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <ArchiveButton
                      itemType="rent_increase"
                      itemId={it.id}
                      itemLabel="cet avis de renouvellement"
                      onArchived={load}
                      size="xs"
                    />
                  </div>
                </td>
              </tr>
              {expandedId === it.id && (
                <tr className="bg-cyan-50/30 border-t border-cyan-100" data-testid={`rent-increase-timeline-row-${it.id}`}>
                  <td colSpan={8} className="px-4 py-3">
                    <NotificationTimeline relatedType="rent_increase" relatedId={it.id} />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
